import { useEffect, useMemo, useState } from "react";
import { RotateCcw } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Separator,
  Textarea,
} from "@/components/ui";
import { formatCurrency, cn } from "@/lib/utils";
import { notify } from "@/lib/notify";
import { useCreateRefund } from "../api";
import type { Order } from "@/types";

interface CreateRefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order | null;
  onCreated?: () => void;
}

function discountRatio(order: Order): number {
  const refundable = order.total - order.tipAmount;

  if (order.subtotal <= 0) {
    return 0;
  }

  return refundable / order.subtotal;
}

function previewLineAmount(
  orderItemSubtotal: number,
  refundQty: number,
  orderQty: number,
  ratio: number,
): number {
  if (orderQty <= 0) {
    return 0;
  }

  return Math.round((orderItemSubtotal * refundQty * ratio) / orderQty);
}

export function CreateRefundDialog({
  open,
  onOpenChange,
  order,
  onCreated,
}: CreateRefundDialogProps) {
  const createRefund = useCreateRefund();
  const [reason, setReason] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    if (open) {
      setReason("");
      setQuantities({});
    }
  }, [open]);

  const items = order?.items ?? [];
  const ratio = order ? discountRatio(order) : 0;

  const totalPreview = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = quantities[item.id] ?? 0;

      return sum + previewLineAmount(item.subtotal, qty, item.quantity, ratio);
    }, 0);
  }, [items, quantities, ratio]);

  const totalPaid = useMemo(
    () =>
      (order?.payments ?? []).reduce(
        (sum, p) => sum + p.amountTendered - p.changeGiven,
        0,
      ),
    [order],
  );

  const selectedCount = Object.values(quantities).reduce(
    (sum, q) => sum + (q > 0 ? 1 : 0),
    0,
  );

  const submittableItems = items
    .map((item) => ({
      orderItemId: item.id,
      quantity: quantities[item.id] ?? 0,
    }))
    .filter((i) => i.quantity > 0);

  const cannotRefund =
    !order ||
    order.status === "cancelled" ||
    totalPaid <= 0 ||
    submittableItems.length === 0 ||
    reason.trim().length === 0 ||
    totalPreview > totalPaid;

  const handleSubmit = async () => {
    if (!order || cannotRefund) {
      return;
    }

    try {
      await createRefund.mutateAsync({
        orderId: order.id,
        reason: reason.trim(),
        items: submittableItems,
      });
      notify.success("Refund created — pending manager approval");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      notify.fromError(err, "Failed to create refund");
    }
  };

  if (!order) {
    return null;
  }

  const orderHasDiscount = order.discountAmount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Refund Order #{order.orderNumber}
          </DialogTitle>
          <DialogDescription>
            Choose how many of each item to refund. The refund amount is
            calculated from what the customer actually paid for the line, after
            any order-level discounts.
          </DialogDescription>
        </DialogHeader>
        {order.status === "cancelled" ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            Cancelled orders can't be refunded.
          </p>
        ) : totalPaid <= 0 ? (
          <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700">
            This order has no payment recorded yet, so there's nothing to
            refund.
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {items.map((item) => {
                const qty = quantities[item.id] ?? 0;
                const effectiveLineTotal = Math.round(item.subtotal * ratio);
                const linePreview = previewLineAmount(
                  item.subtotal,
                  qty,
                  item.quantity,
                  ratio,
                );

                return (
                  <div
                    key={item.id}
                    className={cn(
                      "flex items-center justify-between rounded-md border p-3",
                      qty > 0 && "border-primary/40 bg-primary/5",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {item.quantity}× {item.productName}
                      </p>
                      {item.variantName && (
                        <p className="text-xs text-muted-foreground">
                          {item.variantName}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Line total: {formatCurrency(effectiveLineTotal)}
                        {orderHasDiscount && (
                          <span className="ml-1 text-muted-foreground/70 line-through">
                            {formatCurrency(item.subtotal)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-end">
                        <Label className="text-[10px] text-muted-foreground">
                          Refund qty
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          max={item.quantity}
                          value={qty || ""}
                          placeholder="0"
                          onChange={(e) => {
                            const v = Math.max(
                              0,
                              Math.min(
                                item.quantity,
                                parseInt(e.target.value) || 0,
                              ),
                            );
                            setQuantities((prev) => ({
                              ...prev,
                              [item.id]: v,
                            }));
                          }}
                          className="h-9 w-20 text-right"
                        />
                      </div>
                      <div className="w-20 text-right font-semibold">
                        {qty > 0 ? formatCurrency(linePreview) : "—"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="refund-reason">
                Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="refund-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this being refunded?"
                rows={2}
              />
            </div>
            <div className="rounded-md bg-muted/50 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Items selected</span>
                <span>{selectedCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Order paid</span>
                <span>{formatCurrency(totalPaid)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between text-base font-semibold">
                <span>Refund total (preview)</span>
                <span>{formatCurrency(totalPreview)}</span>
              </div>
              {totalPreview > totalPaid && (
                <p className="mt-1 text-xs text-destructive">
                  Refund preview exceeds amount paid.
                </p>
              )}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={cannotRefund || createRefund.isPending}
          >
            {createRefund.isPending ? "Submitting…" : "Submit refund request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
